import { dataService, Order, Payment, RawMaterial, Customer } from './dataService';

export interface BusinessData {
  todaySales: number;
  todayOrders: number;
  yesterdaySales: number;
  yesterdayOrders: number;
  yesterdayTopSpender: {name: string, amount: number} | null;
  totalRevenue: number;
  averageOrderValue: number;
  lowStockItems: Array<{name: string, currentStock: number, minStock: number}>;
  inventoryValue: number;
  activeCustomers: number;
  totalCustomers: number;
  popularItems: Array<{name: string, quantity: number, revenue: number}>;
  topCustomers: Array<{name: string, totalSpent: number, orderCount: number}>;
  unpaidCustomers: Array<{name: string, unpaidAmount: number, unpaidOrders: number, totalSpent: number}>;
  unpaidOrders: Array<{id: string, customer: string, total: number, paidAmount: number, remainingAmount: number, createdAt: Date, status: string}>;
  totalUnpaidAmount: number;
  revenueTrends: Array<{date: string, revenue: number}>;
  recentOrders: Order[];
  businessName: string;
  businessType: string;
}

class BusinessIntelligenceService {
  
  async getBusinessData(tenantId: string): Promise<BusinessData> {
    try {
      
      // Fetch all necessary data in parallel
      const [orders, payments, rawMaterials, customers] = await Promise.all([
        dataService.getOrders(tenantId).catch(err => {
          console.error('Error fetching orders:', err);
          return [] as Order[];
        }),
        dataService.getPayments(tenantId).catch(err => {
          console.error('Error fetching payments:', err);
          return [] as Payment[];
        }),
        dataService.getRawMaterials(tenantId).catch(err => {
          console.error('Error fetching raw materials:', err);
          return [] as RawMaterial[];
        }),
        dataService.getCustomers(tenantId).catch(err => {
          console.error('Error fetching customers:', err);
          return [] as Customer[];
        })
      ]) as [Order[], Payment[], RawMaterial[], Customer[]];


      // Calculate today's metrics
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const todayOrders = orders.filter(order => {
        if (!order.createdAt) return false;
        const orderDate = new Date(order.createdAt);
        return orderDate >= today && orderDate < tomorrow;
      });

      const todayPayments = payments.filter(payment => {
        if (!payment.createdAt) return false;
        const paymentDate = new Date(payment.createdAt);
        return paymentDate >= today && paymentDate < tomorrow;
      });

      // Calculate yesterday's metrics
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const yesterdayOrders = orders.filter(order => {
        if (!order.createdAt) return false;
        const orderDate = new Date(order.createdAt);
        return orderDate >= yesterday && orderDate < today;
      });

      const yesterdayPayments = payments.filter(payment => {
        if (!payment.createdAt) return false;
        const paymentDate = new Date(payment.createdAt);
        return paymentDate >= yesterday && paymentDate < today;
      });

      // Calculate yesterday's customer spending
      const yesterdayCustomerSpending = new Map<string, number>();
      yesterdayOrders.forEach(order => {
        const existing = yesterdayCustomerSpending.get(order.customer) || 0;
        yesterdayCustomerSpending.set(order.customer, existing + (order.total || 0));
      });

      const yesterdayTopSpender = Array.from(yesterdayCustomerSpending.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 1)[0];

      const yesterdaySales = yesterdayPayments.reduce((sum: number, payment: Payment) => sum + (payment.amount || 0), 0);

      const todaySales = todayPayments.reduce((sum: number, payment: Payment) => sum + (payment.amount || 0), 0);
      const totalRevenue = (payments as Payment[]).reduce((sum: number, payment: Payment) => sum + (payment.amount || 0), 0);
      const averageOrderValue = orders.length > 0 ? totalRevenue / orders.length : 0;

      // Calculate low stock items
      const lowStockItems = rawMaterials
        .filter(material => material.currentStock <= material.minStock)
        .map(material => ({
          name: material.name,
          currentStock: material.currentStock,
          minStock: material.minStock
        }));

      // Calculate inventory value
      const inventoryValue = (rawMaterials as RawMaterial[]).reduce((sum: number, material: RawMaterial) => 
        sum + (material.currentStock * (material.costPerSubUnit || material.costPerUnit || 0)), 0
      );

      // Calculate active customers (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const recentOrderCustomers = new Set(
        orders
          .filter(order => order.createdAt && new Date(order.createdAt) >= thirtyDaysAgo)
          .map(order => order.customer)
          .filter(customer => customer)
      );

      // Calculate popular items
      const itemSales = new Map<string, { quantity: number; revenue: number }>();
      
      orders.forEach(order => {
        if (order.items && Array.isArray(order.items)) {
          order.items.forEach(item => {
            const existing = itemSales.get(item.menuItemName) || { quantity: 0, revenue: 0 };
            itemSales.set(item.menuItemName, {
              quantity: existing.quantity + (item.quantity || 0),
              revenue: existing.revenue + (item.totalPrice || 0)
            });
          });
        }
      });

      const popularItems = Array.from(itemSales.entries())
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.quantity - a.quantity)
        .slice(0, 5);

      // Calculate top customers and unpaid customers
      const customerStats = new Map<string, { totalSpent: number; orderCount: number; unpaidAmount: number; unpaidOrders: number }>();
      
      orders.forEach(order => {
        const existing = customerStats.get(order.customer) || { totalSpent: 0, orderCount: 0, unpaidAmount: 0, unpaidOrders: 0 };
        const isPaid = order.isPaid || order.paidAmount >= order.total;
        const unpaidAmount = isPaid ? 0 : (order.total - (order.paidAmount || 0));
        
        customerStats.set(order.customer, {
          totalSpent: existing.totalSpent + (order.total || 0),
          orderCount: existing.orderCount + 1,
          unpaidAmount: existing.unpaidAmount + unpaidAmount,
          unpaidOrders: existing.unpaidOrders + (isPaid ? 0 : 1)
        });
      });

      const topCustomers = Array.from(customerStats.entries())
        .map(([name, data]) => ({ name, totalSpent: data.totalSpent, orderCount: data.orderCount }))
        .sort((a, b) => b.totalSpent - a.totalSpent)
        .slice(0, 5);

      // Get customers with unpaid amounts
      const unpaidCustomers = Array.from(customerStats.entries())
        .filter(([name, data]) => data.unpaidAmount > 0)
        .map(([name, data]) => ({ 
          name, 
          unpaidAmount: data.unpaidAmount, 
          unpaidOrders: data.unpaidOrders,
          totalSpent: data.totalSpent 
        }))
        .sort((a, b) => b.unpaidAmount - a.unpaidAmount);

      // Get unpaid orders
      const unpaidOrders = orders.filter(order => {
        const isPaid = order.isPaid || (order.paidAmount >= order.total);
        return !isPaid;
      }).map(order => ({
        id: order.id || '',
        customer: order.customer,
        total: order.total,
        paidAmount: order.paidAmount || 0,
        remainingAmount: order.total - (order.paidAmount || 0),
        createdAt: order.createdAt || new Date(),
        status: order.status
      }));

      // Calculate revenue trends (last 7 days)
      const revenueTrends = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);
        
        const nextDate = new Date(date);
        nextDate.setDate(nextDate.getDate() + 1);
        
        const dayPayments = payments.filter(payment => {
          if (!payment.createdAt) return false;
          const paymentDate = new Date(payment.createdAt);
          return paymentDate >= date && paymentDate < nextDate;
        });
        
        const dayRevenue = (dayPayments as Payment[]).reduce((sum: number, payment: Payment) => sum + (payment.amount || 0), 0);
        
        revenueTrends.push({
          date: date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
          revenue: dayRevenue
        });
      }

      // Get recent orders
      const recentOrders = orders
        .sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB.getTime() - dateA.getTime();
        })
        .slice(0, 10);

      return {
        todaySales,
        todayOrders: todayOrders.length,
        yesterdaySales,
        yesterdayOrders: yesterdayOrders.length,
        yesterdayTopSpender: yesterdayTopSpender ? {
          name: yesterdayTopSpender[0],
          amount: yesterdayTopSpender[1]
        } : null,
        totalRevenue,
        averageOrderValue,
        lowStockItems,
        inventoryValue,
        activeCustomers: recentOrderCustomers.size,
        totalCustomers: customers.length,
        popularItems,
        topCustomers,
        unpaidCustomers,
        unpaidOrders,
        totalUnpaidAmount: unpaidCustomers.reduce((sum, customer) => sum + customer.unpaidAmount, 0),
        revenueTrends,
        recentOrders,
        businessName: 'CafeLawn',
        businessType: 'cafe'
      };

    } catch (error) {
      return this.getDefaultBusinessData();
    }
  }

  private getDefaultBusinessData(): BusinessData {
    return {
      todaySales: 0,
      todayOrders: 0,
      yesterdaySales: 0,
      yesterdayOrders: 0,
      yesterdayTopSpender: null,
      totalRevenue: 0,
      averageOrderValue: 0,
      lowStockItems: [],
      inventoryValue: 0,
      activeCustomers: 0,
      totalCustomers: 0,
      popularItems: [],
      topCustomers: [],
      unpaidCustomers: [],
      unpaidOrders: [],
      totalUnpaidAmount: 0,
      revenueTrends: [],
      recentOrders: [],
      businessName: 'CafeLawn',
      businessType: 'cafe'
    };
  }

  // Get specific data for different query types
  async getSalesData(tenantId: string): Promise<any> {
    const businessData = await this.getBusinessData(tenantId);
    return {
      todaySales: businessData.todaySales,
      todayOrders: businessData.todayOrders,
      totalRevenue: businessData.totalRevenue,
      averageOrderValue: businessData.averageOrderValue,
      revenueTrends: businessData.revenueTrends
    };
  }

  async getInventoryData(tenantId: string): Promise<any> {
    const businessData = await this.getBusinessData(tenantId);
    return {
      lowStockItems: businessData.lowStockItems,
      inventoryValue: businessData.inventoryValue
    };
  }

  async getCustomerData(tenantId: string): Promise<any> {
    const businessData = await this.getBusinessData(tenantId);
    return {
      activeCustomers: businessData.activeCustomers,
      totalCustomers: businessData.totalCustomers,
      topCustomers: businessData.topCustomers
    };
  }

  async getPopularItemsData(tenantId: string): Promise<any> {
    const businessData = await this.getBusinessData(tenantId);
    return {
      popularItems: businessData.popularItems
    };
  }
}

export const businessIntelligenceService = new BusinessIntelligenceService();
export default businessIntelligenceService; 